import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { assign } from '../../../shared/Assign';
import Box from '@mui/material/Box';
import { formatDate, Translate } from '../../../shared/LanguageService';
import { connect } from 'react-redux';
import CreateBookingButtons from '../se360/components/DuskPhotographyBookingComponents/CreateBookingButtons';
import Divider from '@mui/material/Divider';
import ForecastWidget from './components/weatherComponent/ForecastWidget';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import SunriseSunsetSection from './components/weatherComponent/SunriseSunsetSection';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import PortfolioModal from '../../photographerPortfolio/PortfolioModal';
import { DatePicker, MobileDatePicker } from '@mui/x-date-pickers';
import IconButton from '@mui/material/IconButton';
import { ChevronLeft, ChevronRight, CalendarToday } from '@mui/icons-material';
import { isToday } from 'date-fns';
import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import SelectablePhotographersSection from './components/DuskPhotographyBookingComponents/SelectablePhotographersSection';
import SendRequestModal from './components/DuskPhotographyBookingComponents/SendRequestModal';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import InputBase from '@mui/material/InputBase';
import { parse } from 'query-string';
import * as OrderActions from '../../orders/OrdersActions';
import * as PhotographerBookingActions from './actions/SE360PhotographerBookingActions';
import LoadingSkeletons from './SE360DuskPhotographerBookingLoadingSkeleton';
import {
    createBooking,
    createWeatherDetails,
    updateBooking
} from './SE360DuskPhotographerBookingHelperFunctions';
import SE360DuskPhotographerBookingUnableToBook from './SE360DuskPhotographerBookingUnableToBook';
import useSE360DuskPhotographyBookingQueries from './useSE360DuskPhotographyBookingQueries';

export type DuskPhotographerBookingLabels = {
    pageTitle: string;
    availablePhotographers: string;
    noAvailablePhotographers: string;
    timeOfBooking: string;
    portfolioButton: string;
    cancelButton: string;
    bookButton: string;
    pageParagraph: string;
    noWeatherInfo: string;
    sendRequest: string;
    requestModalTitle: string;
    requestModalText: string;
    requestModalDate: string;
    requestModalInfo: string;
    errorMessageUnableToBook: string;
};

type Photographer = {
    name: string;
    id: number;
    profileImageReference: string;
};

type DuskPhotographerBookingProps = {
    photographers: Photographer[];
    latitude: number;
    longitude: number;
    getPhotographers: (bookingDate) => void;
    sunset: string | null;
    sunrise: string | null;
    forecasts: [];
    labels: DuskPhotographerBookingLabels;
    duskPhotographyStartTime: string;
    duskPhotographyEndTime: string;
    getNewWeatherData: (bookingDate) => void;
    bookingDate: any;
    isSmallerScreen: boolean;
    orderCommandForBooking: any;
    createBooking: any;
    updateBooking: any;
    calendarItemStartTime: Date;
    calendarItemEndTime: Date;
    orderId: number;
};

let calendarItemType = {
    available: 0,
    se360Booking: 1 << 0,
    scandphotosBooking: 1 << 1,
    blocked: 1 << 2,
    travel: 1 << 3
};

export const StyledContainerGrid = styled(Grid)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '457px',
    minWidth: 0,
    margin: 'auto',
    padding: '24px'
}));

export const StyledContainerGridItem = styled(Grid)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    minHeight: '72px',
    margin: 'auto'
}));

const today = moment({ h: 0, m: 0, s: 0, ms: 0 }).toDate();
const tomorrow = moment({ h: 0, m: 0, s: 0, ms: 0 }).add(1, 'days').toDate();

export const PageTitleAndText = ({ pageTitle, pageParagraph }) => {
    return (
        <>
            <Typography
                sx={{
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: '32px',
                    fontFamily: 'Gilroy'
                }}
            >
                {pageTitle}
            </Typography>
            <Typography
                sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '24px',
                    marginTop: '8px',
                    whiteSpace: 'pre-wrap'
                }}
            >
                {pageParagraph}
            </Typography>
        </>
    );
};

const getInitiallySelectedPhotographer = (photographers) => {
    let firstPhotographerItem =
        photographers !== null && photographers && photographers.find((x) => x !== undefined);

    return firstPhotographerItem ? firstPhotographerItem['id'] : '';
};

export const SE360DuskPhotographerBooking = ({
    photographers,
    getPhotographers,
    sunset,
    sunrise,
    forecasts,
    labels,
    duskPhotographyStartTime,
    duskPhotographyEndTime,
    bookingDate,
    isSmallerScreen,
    orderCommandForBooking,
    createBooking,
    calendarItemStartTime,
    calendarItemEndTime,
    orderId,
    updateBooking,
    history
}: DuskPhotographerBookingProps & RouteComponentProps) => {
    const [selectedPhotographer, setSelectedPhotographer] = React.useState(
        getInitiallySelectedPhotographer(photographers)
    );
    const [photographerModal, setPhotographerModal] = React.useState({
        name: '',
        id: null
    });
    const [showPortfolioModal, setShowPortfolioModal] = React.useState(false);
    const [toggleStyling, setToggleStyling] = React.useState(0);

    let selectedDay = bookingDate && formatDate(new Date(bookingDate), 'EEEE');
    let formattedSelectedDate = bookingDate && formatDate(new Date(bookingDate), 'dd MMMM yyyy');

    const onDateChange = (newValue) => {
        getPhotographers(newValue);
        setToggleStyling(0);
    };

    const handleNextDayClick = () => {
        let newDate = addDays(bookingDate, 1);
        onDateChange(newDate);
    };

    const handlePreviousDayClick = () => {
        let newDate = subDays(bookingDate, 1);
        onDateChange(newDate);
    };

    const handleSelectPhotographer = (event) => {
        setSelectedPhotographer(event.target.value);
        let index = photographers.findIndex((obj) => obj.id == event.target.value);
        setToggleStyling(index);
    };

    const handleSelectPhotographerClick = (id, index) => {
        setSelectedPhotographer(id);
        setToggleStyling(index);
    };

    const openPhotographerPortfolioModal = (
        event,
        photographerName: string,
        photographerId: any
    ) => {
        event.stopPropagation();
        setShowPortfolioModal(true);
        setPhotographerModal({ name: photographerName, id: photographerId });
    };

    const handleCancel = () => {
        if (orderCommandForBooking) {
            history.push('/orders/new');
        } else {
            history.push(`/orders/${orderId}/basic`);
        }
    };

    const handleBook = () => {
        let calendarItem = {
            id: 0,
            orderId: orderId ? orderId : null,
            start: calendarItemStartTime,
            end: calendarItemEndTime,
            userId: selectedPhotographer,
            type: calendarItemType.se360Booking
        };

        if (orderCommandForBooking) {
            let newOrderCommandForBooking = assign({}, orderCommandForBooking);
            newOrderCommandForBooking.calendarItem = calendarItem;
            createBooking(newOrderCommandForBooking);
        }

        if (orderId) {
            const updatedItem = {
                calendarItem: calendarItem,
                isAdvanced: false,
                planOrderId: null
            };
            updateBooking(updatedItem);
        }
    };

    const [showSendRequestModal, setShowSendRequestModal] = React.useState(false);

    return (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <StyledContainerGrid container sx={{ width: '100%', overflowX: 'hidden', margin: 0 }}>
            <StyledContainerGridItem
                item
                flexDirection={'column'}
                alignItems={'flex-start'}
                sx={{ padding: '0px 10px 0px 10px' }}
            >
                <PageTitleAndText
                    pageTitle={labels.pageTitle}
                    pageParagraph={labels.pageParagraph}
                />
            </StyledContainerGridItem>
            <Divider sx={{ alignItems: 'center', margin: '24px 8px 10px 8px' }} />
            <StyledContainerGridItem item alignItems={'center'} sx={{ marginTop: '10px' }}>
                {isSmallerScreen ? (
                    <MobileDatePicker
                        value={bookingDate}
                        disablePast={true}
                        onChange={(newValue) => onDateChange(newValue)}
                        renderInput={(params) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    height: '72px',
                                    alignItems: 'center',
                                    justifyContent: 'space-evenly',
                                    backgroundColor: '#F5F5F5',
                                    borderRadius: '4px',
                                    marginBottom: '22px',
                                    position: 'relative'
                                }}
                            >
                                <Box>
                                    <IconButton disabled={isToday(bookingDate)}>
                                        <ChevronLeft onClick={handlePreviousDayClick} />
                                    </IconButton>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            paddingRight: '7px',
                                            fontSize: '15px',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        {selectedDay},
                                    </Typography>
                                    <Typography sx={{ fontWeight: 400 }}>
                                        {formattedSelectedDate}
                                    </Typography>
                                </Box>

                                <Box>
                                    <IconButton>
                                        <ChevronRight onClick={handleNextDayClick} />
                                    </IconButton>
                                </Box>
                                <InputBase
                                    {...params}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            display: 'none',
                                            width: 0,
                                            margin: '0px 10px 0px 10px'
                                        }
                                    }}
                                    endAdornment={<CalendarToday />}
                                />
                            </Box>
                        )}
                    />
                ) : (
                    <DatePicker
                        disablePast={true}
                        label="Custom input"
                        value={bookingDate}
                        onChange={(newValue) => onDateChange(newValue)}
                        renderInput={({ inputRef, InputProps }) => (
                            <Box
                                ref={inputRef}
                                sx={{
                                    display: 'flex',
                                    minWidth: '100%',
                                    height: '72px',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#F5F5F5',
                                    borderRadius: '4px',
                                    marginBottom: '22px',
                                    position: 'relative'
                                }}
                            >
                                <Box sx={{ display: 'flex', position: 'absolute', left: '50px' }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            paddingRight: '7px',
                                            fontSize: '15px',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        {selectedDay},
                                    </Typography>
                                    <Typography sx={{ fontWeight: 400 }}>
                                        {formattedSelectedDate}
                                    </Typography>
                                </Box>
                                <IconButton
                                    disabled={isToday(bookingDate)}
                                    onClick={handlePreviousDayClick}
                                    sx={{ position: 'absolute', right: '140px' }}
                                >
                                    <ChevronLeft />
                                </IconButton>
                                <IconButton
                                    onClick={handleNextDayClick}
                                    sx={{ position: 'absolute', right: '96px' }}
                                >
                                    <ChevronRight />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: '60px',
                                        '& .MuiSvgIcon-root': {
                                            height: '24px',
                                            width: 'auto',
                                            color: '#333D45'
                                        }
                                    }}
                                >
                                    {InputProps && InputProps.endAdornment}
                                </Box>
                            </Box>
                        )}
                    />
                )}
            </StyledContainerGridItem>
            {sunrise && sunset ? (
                <Grid
                    item
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        minWidth: '100%',
                        height: '72px',
                        margin: 'auto',
                        gap: '5px'
                    }}
                    aria-label={'sunrise-sunset-times'}
                >
                    <SunriseSunsetSection sunrise={sunrise} sunset={sunset} labels={'n/a'} />
                    <ForecastWidget
                        selectedForecasts={forecasts != null ? forecasts : []}
                        city={''}
                        labels={labels}
                    />
                </Grid>
            ) : (
                <Skeleton
                    variant="rectangular"
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        paddingTop: '32px 0px 20px 0px',
                        margin: 'auto',
                        width: '100%',
                        height: '72px'
                    }}
                />
            )}
            {duskPhotographyStartTime && duskPhotographyEndTime ? (
                <StyledContainerGridItem
                    item
                    alignItems={'center'}
                    sx={{
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid rgba(33, 150, 243, 0.5)',
                        margin: '10px auto auto auto',
                        padding: '0px 40px 0px 18px'
                    }}
                >
                    <Typography
                        aria-label={'booking-time-text'}
                        id={'booking-text'}
                        sx={{
                            fontFamily: 'Gilroy',
                            fontWeight: 700,
                            fontSize: '16px',
                            flex: 1
                        }}
                    >
                        {labels.timeOfBooking}
                    </Typography>
                    <Typography
                        aria-label={'booking-time-hour'}
                        id={'booking-hour'}
                        sx={{
                            fontFamily: 'Gilroy',
                            fontWeight: 700,
                            fontSize: '16px'
                        }}
                    >
                        {duskPhotographyStartTime} - {duskPhotographyEndTime}
                    </Typography>
                </StyledContainerGridItem>
            ) : (
                <Skeleton
                    variant="rectangular"
                    sx={{
                        display: 'flex',
                        margin: '10px auto auto auto',
                        borderRadius: '6px',
                        padding: '0px 40px 0px 18px',
                        width: '100%',
                        height: '72px'
                    }}
                />
            )}
            <Divider sx={{ alignItems: 'center', margin: '24px 8px 10px 8px' }} />
            <StyledContainerGridItem item alignItems={'center'} flexDirection={'column'}>
                {photographers === null ? (
                    <Skeleton
                        variant="rectangular"
                        sx={{
                            display: 'flex',
                            margin: '16px auto auto auto',
                            borderRadius: '6px',
                            padding: '0px 40px 0px 18px',
                            width: '100%',
                            height: '72px'
                        }}
                    />
                ) : photographers && photographers.length < 1 ? (
                    <Typography
                        aria-label={'no-available-photographers'}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            margin: 'auto',
                            textTransform: 'uppercase',
                            fontWeight: 400,
                            fontSize: '1.4rem',
                            lineHeight: '32px',
                            color: 'rgba(0, 0, 0, 0.6)',
                            width: '100%'
                        }}
                    >
                        {labels.noAvailablePhotographers}
                    </Typography>
                ) : (
                    <SelectablePhotographersSection
                        photographers={photographers ? photographers : []}
                        selectedPhotographer={selectedPhotographer}
                        onSelectPhotographer={handleSelectPhotographer}
                        availablePhotographersLabel={labels.availablePhotographers}
                        onSelectPhotographerClick={handleSelectPhotographerClick}
                        openPhotographerPortfolioModal={openPhotographerPortfolioModal}
                        portfolioButtonLabel={labels.portfolioButton}
                        toggleStyling={toggleStyling}
                    />
                )}
            </StyledContainerGridItem>
            {photographers && true && photographers.length < 1 ? (
                <Divider sx={{ alignItems: 'center', margin: '2px 8px 10px 8px' }} />
            ) : null}
            {photographers && photographers !== null ? (
                <>
                    <Grid
                        sx={{
                            display: 'flex',
                            flexDirection: isSmallerScreen ? 'column-reverse' : 'row',
                            alignItems: 'center',
                            justifyContent: orderCommandForBooking ? 'space-between' : 'end'
                        }}
                    >
                        {orderCommandForBooking ? (
                            <Box
                                aria-label="send-request-link"
                                role={'button'}
                                sx={{
                                    fontSize: isSmallerScreen ? '19px' : '15px',
                                    fontWeight: '500',
                                    color: '#4F7C6D',
                                    fontFamily: "'Roboto'",
                                    margin: 'auto',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        cursor: 'pointer'
                                    }
                                }}
                                onClick={() => setShowSendRequestModal(true)}
                            >
                                {labels.sendRequest}
                            </Box>
                        ) : null}
                        <CreateBookingButtons
                            cancelButtonLabel={labels.cancelButton}
                            bookButtonLabel={labels.bookButton}
                            onCancel={handleCancel}
                            onBook={handleBook}
                            photographers={photographers}
                        />
                    </Grid>
                    <SendRequestModal
                        labels={labels}
                        show={showSendRequestModal}
                        onClose={() => setShowSendRequestModal(false)}
                        orderCommandForBooking={orderCommandForBooking}
                        createBookingRequest={createBooking}
                    />
                </>
            ) : null}
            {showPortfolioModal ? (
                <PortfolioModal
                    show={showPortfolioModal}
                    photographer={photographerModal}
                    onClose={() => {
                        setShowPortfolioModal(false);
                    }}
                />
            ) : null}
            </StyledContainerGrid>
        </div>
    );
};

const DuskPhotographerBookingDataLayer = ({
    location,
    orderCommandForBooking,
    labels,
    ...props
}: any) => {
    const params = parse(location.search);

    let executionTime = null;
    let initialState = new Date(tomorrow);

    const [bookingDate, setBookingDate] = React.useState(initialState);
    const [showLoading, setShowLoading] = React.useState(true);

    const onDateChange = (newDate) => {
        setBookingDate(newDate);
        setShowLoading(false);
    };

    const handleCreateBooking = (order) => {
        return createBooking(
            order,
            props.actions.createOrder,
            props.actions.removeOrderCommandForBooking,
            props.history
        );
    };

    const handleUpdateBooking = (updatedCalendarItem) => {
        updateBooking(
            updatedCalendarItem,
            props.actions.updatePhotographer,
            updatedCalendarItem.calendarItem.orderId,
            props.history
        );
    };

    let theme = useTheme();
    let isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

    let {
        newBooking,
        locationData,
        newBookingIsLoading,
        newBookingIsError,
        addressCoordinatesIsLoading,
        addressCoordinatesIsError,
        locationDataIsLoading,
        locationDataIsError
    } = useSE360DuskPhotographyBookingQueries(
        params.orderId,
        bookingDate,
        orderCommandForBooking,
        executionTime
    );

    let photographers = newBooking && newBooking.photographerBookingDtos;
    let orderInfo = newBooking && newBooking.orderInfo;

    let {
        startTime,
        endTime,
        sunrise,
        sunset,
        selectedForecasts,
        formattedStartTime,
        formattedEndTime
    } = createWeatherDetails(orderInfo, locationData);

    if (bookingDate.getDate() === today.getDate()) {
        photographers = [];
    }

    return (showLoading && newBookingIsLoading) ||
        (showLoading && addressCoordinatesIsLoading) ||
        (showLoading && locationDataIsLoading) ? (
        <LoadingSkeletons labels={labels} />
    ) : newBookingIsError || addressCoordinatesIsError || locationDataIsError ? (
        <SE360DuskPhotographerBookingUnableToBook
            isSmallerScreen={isSmallerScreen}
            labels={labels}
            orderCommandForBooking={orderCommandForBooking}
            createBooking={handleCreateBooking}
        />
    ) : (
        <SE360DuskPhotographerBooking
            {...props}
            key={
                photographers &&
                photographers.map((photographer) => photographer.id).join(',') + `${bookingDate}`
            }
            photographers={photographers}
            getPhotographers={(bookingDate) => onDateChange(bookingDate)}
            bookingDate={bookingDate}
            sunrise={sunrise}
            sunset={sunset}
            duskPhotographyStartTime={formattedStartTime}
            duskPhotographyEndTime={formattedEndTime}
            forecasts={selectedForecasts}
            createBooking={handleCreateBooking}
            updateBooking={handleUpdateBooking}
            calendarItemStartTime={startTime}
            calendarItemEndTime={endTime}
            labels={labels}
            isSmallerScreen={isSmallerScreen}
            orderInfo={orderInfo}
            orderCommandForBooking={orderCommandForBooking}
            orderId={params.orderId}
            locationData={locationData}
        />
    );
};

const DuskPhotographerBookingTranslationLayer = (props): any => {
    let labels: DuskPhotographerBookingLabels = {
        pageTitle: Translate('se360/dusk-photographer-booking/page-title'),
        availablePhotographers: Translate(
            'se360/dusk-photographer-booking/available-photographers'
        ),
        noAvailablePhotographers: Translate(
            'se360/dusk-photographer-booking/no-available-photographers'
        ),
        timeOfBooking: Translate('se360/dusk-photographer-booking/bookable-time'),
        portfolioButton: Translate('se360/dusk-photographer-booking/portfolio-button'),
        cancelButton: Translate('se360/dusk-photographer-booking/cancel-button'),
        bookButton: Translate('se360/dusk-photographer-booking/book-button'),
        pageParagraph: Translate('se360/dusk-photographer-booking/page-paragraph'),
        noWeatherInfo: Translate('se360/dusk-photographer-booking/no-weather-info'),
        sendRequest: Translate('se360/dusk-photographer-booking/send-request'),
        requestModalTitle: Translate('se360/dusk-photographer-booking/request-modal-title'),
        requestModalText: Translate('se360/dusk-photographer-booking/request-modal-text'),
        requestModalDate: Translate('se360/dusk-photographer-booking/request-modal-date'),
        requestModalInfo: Translate('se360/dusk-photographer-booking/request-modal-info'),
        errorMessageUnableToBook: Translate('se360/dusk-photographer-booking/unable-to-book')
    };

    return <DuskPhotographerBookingDataLayer {...props} labels={labels} />;
};

function mapStateToProps(state: any) {
    return {
        errors: state.SE360PhotographerBooking.errors,
        orderCommandForBooking: state.orders.orderCommandForBooking,
        addressPointLocation: state.SE360PhotographerBooking.addressPointLocation,
        locationData: state.SE360PhotographerBooking.locationData
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            createOrder: (order: any, orderCollectionId?: number) =>
                dispatch(OrderActions.createOrder(order, orderCollectionId)),
            removeOrderCommandForBooking: () =>
                dispatch(OrderActions.removeOrderCommandForBooking()),
            updatePhotographer: (updatedCalendarItem: any) =>
                dispatch(PhotographerBookingActions.updatePhotographer(updatedCalendarItem))
        }
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRouter(DuskPhotographerBookingTranslationLayer));
